/**
 * Socket.io Provider
 *
 * React context provider for Socket.io connection.
 * Automatically connects when user is authenticated.
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth0 } from '@auth0/auth0-react';

// ============================================
// CONTEXT
// ============================================

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
  subscribe: (type: 'sop' | 'assessment', id: string) => void;
  unsubscribe: (type: 'sop' | 'assessment', id: string) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  connectionError: null,
  subscribe: () => {},
  unsubscribe: () => {},
});

// ============================================
// PROVIDER
// ============================================

interface SocketProviderProps {
  children: ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Initialize socket connection when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Disconnect if user logs out
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    let mounted = true;
    let newSocket: Socket | null = null;

    const initSocket = async () => {
      try {
        // Get fresh access token
        const token = await getAccessTokenSilently();

        // Determine API URL
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

        // Create socket connection with auth token
        newSocket = io(apiUrl, {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        }) as Socket;

        // Connection handlers
        newSocket.on('connect', () => {
          if (mounted) {
            console.log('[Socket] Connected');
            setIsConnected(true);
            setConnectionError(null);
          }
        });

        newSocket.on('disconnect', (reason) => {
          if (mounted) {
            console.log('[Socket] Disconnected:', reason);
            setIsConnected(false);
          }
        });

        newSocket.on('connect_error', (error) => {
          if (mounted) {
            console.error('[Socket] Connection error:', error.message);
            setConnectionError(error.message);
            setIsConnected(false);
          }
        });

        if (mounted) {
          setSocket(newSocket);
        }
      } catch (error) {
        console.error('[Socket] Failed to initialize:', error);
        if (mounted) {
          setConnectionError(error instanceof Error ? error.message : 'Failed to connect');
        }
      }
    };

    initSocket();

    // Cleanup on unmount
    return () => {
      mounted = false;
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  // Subscribe to a specific resource
  const subscribe = useCallback(
    (type: 'sop' | 'assessment', id: string) => {
      if (socket && isConnected) {
        socket.emit('subscribe', { type, id });
      }
    },
    [socket, isConnected]
  );

  // Unsubscribe from a specific resource
  const unsubscribe = useCallback(
    (type: 'sop' | 'assessment', id: string) => {
      if (socket && isConnected) {
        socket.emit('unsubscribe', { type, id });
      }
    },
    [socket, isConnected]
  );

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        connectionError,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useSocketContext(): SocketContextType {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocketContext must be used within a SocketProvider');
  }
  return context;
}
